import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ScrollTopButtonComponent } from '@shared/components/scroll-top-button/scroll-top-button.component';
import { ActivatedRoute } from '@angular/router';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { Store } from '@ngxs/store';
import { FetchUpdates } from '@store/catalogs/catalogs.actions';
import {
  catchError,
  EMPTY,
  filter,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TransitPointService } from '@shared/services/transit-point.service';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlService } from '@shared/services/control.service';
import { CONTROL_SERVICE_PROVIDER } from '@shared/providers/control.provider';
import { VALIDATION_PROVIDER } from '@shared/providers/validate-cargo.provider';
import { TuiButtonModule } from '@taiga-ui/core';
import { WebsocketService } from '@shared/services/websocket.service';
import { CargoCharacteristicsComponent } from './components/cargo-characteristics/cargo-characteristics.component';
import { MainInfoComponent } from './components/main-info/main-info.component';
import { CargoLoadingService } from './services/cargo-loading.service';
import { LoadingAddressesComponent } from './components/loading-addresses/loading-addresses.component';
import { UnloadingAddressesComponent } from './components/unloading-addresses/unloading-addresses.component';
import { TransitPointsComponent } from './components/transit-points/transit-points.component';
import { CarrierComponent } from './components/carrier/carrier.component';
import { DriversReportService } from './services/drivers-report.service';
import { OrdersComponent } from './components/orders/orders.component';
import { CommonStateService } from './services/common-state.service';
import { ControlComponent } from './components/control/control.component';
import { WebsocketEvent } from '../../types/websocket';

@Component({
  selector: 'app-cargo-loading',
  standalone: true,
  imports: [
    ScrollTopButtonComponent,
    MainInfoComponent,
    CargoCharacteristicsComponent,
    LoadingAddressesComponent,
    UnloadingAddressesComponent,
    TransitPointsComponent,
    CarrierComponent,
    OrdersComponent,
    ControlComponent,
    TuiButtonModule,
  ],
  providers: [
    CargoLoadingService,
    DriversReportService,
    {
      provide: VALIDATION_PROVIDER,
      useExisting: CargoLoadingService,
    },
    ControlService,
    {
      provide: CONTROL_SERVICE_PROVIDER,
      useExisting: ControlService,
    },
  ],
  templateUrl: './cargo-loading.component.html',
  styleUrl: './cargo-loading.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CargoLoadingComponent implements OnInit, OnDestroy {
  generateTransportOrder$ = new Subject<void>();
  constructor(
    private readonly cargoLoadingService: CargoLoadingService,
    private readonly route: ActivatedRoute,
    private readonly cargoCatalogsService: CargoCatalogsService,
    private readonly store: Store,
    private readonly transitPointService: TransitPointService,
    private readonly commonStateService: CommonStateService,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly ws: WebsocketService,
  ) {}

  onGenerateTransportOrder() {
    return this.generateTransportOrder$.pipe(
      switchMap(() =>
        this.cargoLoadingService.generateTransportOrder(this.getCargoId()).pipe(
          catchError(err => {
            this.alert.showError(err.error);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  getCargoId() {
    return this.route.snapshot.params.id as number;
  }

  refreshPreOrders() {
    this.commonStateService.refreshPreOrders$
      .pipe(
        startWith(true),
        switchMap(() =>
          this.cargoLoadingService.fetchPreOrdersByCargoId(this.getCargoId()),
        ),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  initSocket(cargoId: number) {
    this.ws.connect().then(() => this.ws.listenCargo(cargoId));
  }

  ngOnDestroy() {
    this.ws.unsubscribeCargo();
  }

  refreshOrdersOnProcessingStatusChange() {
    return this.ws.cargoEvents$
      .pipe(
        filter(
          wsEvent =>
            wsEvent.event === WebsocketEvent.INVOICE_PROCESSING_CHANGE_STATUS,
        ),
        switchMap(() =>
          this.cargoLoadingService.fetchOrdersByCargoId(this.getCargoId()).pipe(
            tap(() =>
              this.alert.showInfo(
                'Обработка инвойса завершена. Заказы обновлены',
              ),
            ),
            catchError(() => {
              this.alert.showError(
                'Обработка инвойса завершена. Не удалось обновить заказы',
              );
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.store.dispatch(new FetchUpdates());
    this.cargoCatalogsService.preload();
    const id = this.getCargoId();
    this.cargoLoadingService.setSelectedCargo(id).pipe(take(1)).subscribe();
    this.cargoLoadingService.fetchOrdersByCargoId(id).pipe(take(1)).subscribe();
    this.transitPointService.preload().pipe(take(1)).subscribe();
    this.refreshPreOrders();
    this.onGenerateTransportOrder()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.initSocket(id);
    this.refreshOrdersOnProcessingStatusChange();
  }
}
